import { ColumnDef } from '@tanstack/react-table';
import {
  CheckCircle,
  Clock,
  Eye,
  MapPin,
  MoreHorizontal,
  Package,
  Pause,
  Pencil,
  Plus,
  Ship,
  Trash2,
  Truck,
  XCircle
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateJobModal from '../components/CreateJobModal';
import Header from '../components/Header';
import { ConfirmDialog, MessageDialog } from '../components/ui/alert-dialog-helper';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { createSortableColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { Job, JobAssignInput, JobPriority, JobStats, JobStatus, JobType, User } from '../types/job';

type Role = { key: string; name: string; description: string };
type UserWithRoles = { id: string; email: string; first_name: string; last_name: string; roles: Role[] };

const Jobs: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [allJobs, setAllJobs] = useState<Job[]>([]); // Store all jobs for filtering
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<UserWithRoles | null>(null);
  const [stats, setStats] = useState<JobStats | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Server-side pagination and sorting state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  // Assignment modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [drivers, setDrivers] = useState<User[]>([]);
  const [supervisors, setSupervisors] = useState<User[]>([]);
  const [assignmentForm, setAssignmentForm] = useState<JobAssignInput>({
    driver_id: '',
    supervisor_id: '',
    planned_start_at: '',
    planned_end_at: '',
  });

  // Create job modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadDriversAndSupervisors();
    loadStats();
    checkAuth();
  }, []);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/jobs/stats', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error loading job stats:', err);
    }
  };

  useEffect(() => {
    loadJobs();
  }, [page, limit, sortBy, sortOrder, debouncedSearch]);

  // Reload when local filters change (server will filter by status/type when supported)
  useEffect(() => {
    loadJobs();
  }, [statusFilter, typeFilter]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder,
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statusFilter !== 'all' && statusFilter !== 'overdue') params.set('status', statusFilter);
      if (typeFilter !== 'all') params.set('job_type', typeFilter);

      const response = await fetch(`/api/jobs?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load jobs');
      }

      const data = await response.json();
      const jobsData = data.success ? data.data : [];
      setAllJobs(jobsData);
      setJobs(jobsData);
      setPagination(data.pagination || { total: 0, page: 1, limit: 10, pages: 0 });
    } catch (err) {
      console.error('Error loading jobs:', err);
    } finally {
      setLoading(false);
    }
  };


  const loadDriversAndSupervisors = async () => {
    try {
      const token = localStorage.getItem('token');
      const [driversResponse, supervisorsResponse] = await Promise.all([
        fetch('/api/users?role=driver', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch('/api/users?role=supervisor', {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (driversResponse.ok) {
        const driversData = await driversResponse.json();
        setDrivers(driversData.users || []);
      }

      if (supervisorsResponse.ok) {
        const supervisorsData = await supervisorsResponse.json();
        setSupervisors(supervisorsData.users || []);
      }
    } catch (err) {
      console.error('Error loading drivers and supervisors:', err);
    }
  };

  const handleJobCreated = () => {
    loadJobs();
  };

  const handleDeleteJob = async () => {
    if (!selectedJob) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/jobs/${selectedJob.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (data.success) {
        setDeleteDialogOpen(false);
        loadJobs();
      } else {
        setDeleteDialogOpen(false);
        setErrorMessage(data.message || 'Failed to delete job');
        setErrorDialogOpen(true);
      }
    } catch (err) {
      console.error('Error deleting job:', err);
      setDeleteDialogOpen(false);
      setErrorMessage('An error occurred while deleting the job');
      setErrorDialogOpen(true);
    }
  };

  const checkAuth = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      }
    } catch (err) {
      console.error('Error reading user data:', err);
    }
  };

  const viewDetails = (job: Job) => {
    navigate(`/jobs/${job.id}`);
  };

  const getJobTypeIcon = (type: JobType | null) => {
    if (!type) return <Package className="h-4 w-4" />;

    switch (type) {
      case JobType.DELIVERY:
        return <Truck className="h-4 w-4" />;
      case JobType.PICKUP:
        return <Ship className="h-4 w-4" />;
      case JobType.INSTALLATION:
        return <Package className="h-4 w-4" />;
      case JobType.MAINTENANCE:
        return <MapPin className="h-4 w-4" />;
      case JobType.TRANSFER:
        return <Package className="h-4 w-4" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  const getJobTypeBadgeVariant = (type: JobType | null) => {
    if (!type) return 'secondary' as const;

    switch (type) {
      case JobType.DELIVERY:
        return 'default' as const;
      case JobType.PICKUP:
        return 'secondary' as const;
      case JobType.INSTALLATION:
        return 'warning' as const;
      case JobType.MAINTENANCE:
        return 'destructive' as const;
      case JobType.TRANSFER:
        return 'outline' as const;
      default:
        return 'secondary' as const;
    }
  };

  const getStatusIcon = (status: JobStatus) => {
    switch (status) {
      case JobStatus.COMPLETED:
        return <CheckCircle className="h-4 w-4" />;
      case JobStatus.IN_PROGRESS:
        return <Clock className="h-4 w-4" />;
      case JobStatus.CANCELLED:
        return <XCircle className="h-4 w-4" />;
      case JobStatus.ON_HOLD:
        return <Pause className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusBadgeVariant = (status: JobStatus) => {
    switch (status) {
      case JobStatus.COMPLETED:
        return 'default' as const;
      case JobStatus.IN_PROGRESS:
        return 'warning' as const;
      case JobStatus.ASSIGNED:
        return 'secondary' as const;
      case JobStatus.CANCELLED:
        return 'destructive' as const;
      case JobStatus.ON_HOLD:
        return 'outline' as const;
      default:
        return 'secondary' as const;
    }
  };

  const getPriorityBadgeVariant = (priority: JobPriority) => {
    switch (priority) {
      case JobPriority.URGENT:
        return 'destructive' as const;
      case JobPriority.HIGH:
        return 'warning' as const;
      case JobPriority.NORMAL:
        return 'secondary' as const;
      case JobPriority.LOW:
        return 'outline' as const;
      default:
        return 'secondary' as const;
    }
  };

  const openAssignModal = (job: Job) => {
    setSelectedJob(job);
    setAssignmentForm({
      driver_id: job.assigned_driver_id || '',
      supervisor_id: job.assigned_supervisor_id || '',
      planned_start_at: job.planned_start_at || '',
      planned_end_at: job.planned_end_at || '',
    });
    setAssignModalOpen(true);
  };

  const handleAssignJob = async () => {
    if (!selectedJob || !assignmentForm.driver_id) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/jobs/${selectedJob.id}/assign`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(assignmentForm),
      });

      if (!response.ok) {
        throw new Error('Failed to assign job');
      }

      setAssignModalOpen(false);
      setSelectedJob(null);
      loadJobs(); // Reload jobs to show updated assignment
    } catch (err) {
      console.error('Error assigning job:', err);
    }
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const handleSortingChange = (newSortBy: string, newSortOrder: 'ASC' | 'DESC') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(1);
  };

  const handleStatsFilterClick = (status: string) => {
    setStatusFilter(status);
    setPage(1);
  };

  const canManageJobs = user?.roles?.some(role => ['admin', 'supervisor', 'dispatcher'].includes(role.key)) ?? false;

  const hasActiveFilters = statusFilter !== 'all' || typeFilter !== 'all';

  const columns: ColumnDef<Job>[] = [
    {
      ...createSortableColumn<Job>('job_number', 'Job Number'),
      cell: ({ row }) => {
        const job = row.original;
        return (
          <button
            onClick={() => viewDetails(job)}
            className="font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
          >
            {job.job_number}
          </button>
        );
      },
    },
    {
      ...createSortableColumn<Job>('job_code', 'Job Code'),
      cell: ({ row }) => row.original.job_code || '—',
    },
    {
      accessorKey: 'job_type',
      header: 'Type',
      cell: ({ row }) => {
        const job = row.original;
        return (
          <div className="flex items-center gap-2">
            {getJobTypeIcon(job.job_type)}
            <Badge variant={getJobTypeBadgeVariant(job.job_type)}>
              {job.job_type ? job.job_type.toUpperCase() : 'TASK-BASED'}
            </Badge>
          </div>
        );
      },
    },
    {
      ...createSortableColumn<Job>('status', 'Status'),
      cell: ({ row }) => {
        const job = row.original;
        return (
          <div className="flex items-center gap-2">
            {getStatusIcon(job.status)}
            <Badge variant={getStatusBadgeVariant(job.status)}>
              {job.status.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
        );
      },
    },
    {
      ...createSortableColumn<Job>('priority', 'Priority'),
      cell: ({ row }) => {
        const job = row.original;
        return (
          <Badge variant={getPriorityBadgeVariant(job.priority)}>
            {job.priority.toUpperCase()}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'shipments',
      header: 'Shipments',
      cell: ({ row }) => {
        const job = row.original;
        const shipmentCount = job.shipments?.length || 0;

        if (shipmentCount === 0) {
          return <span className="text-muted-foreground">No shipments</span>;
        }

        if (shipmentCount === 1 && job.shipments && job.shipments[0]) {
          const shipment = job.shipments[0];
          return (
            <button
              onClick={() => navigate(`/shipments/${shipment.id}`)}
              className="text-blue-600 hover:text-blue-800 underline hover:no-underline"
            >
              {shipment.bl_number}
            </button>
          );
        }

        return (
          <span className="text-sm">
            {shipmentCount} shipments
          </span>
        );
      },
    },
    {
      accessorKey: 'order',
      header: 'Order',
      cell: ({ row }) => {
        const job = row.original;
        if (!job.order || !job.order.order_number) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <button
            onClick={() => navigate(`/orders/${job.order!.id}`)}
            className="font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
          >
            {job.order.order_number}
          </button>
        );
      },
    },
    {
      accessorKey: 'tasks',
      header: 'Tasks',
      cell: ({ row }) => {
        const job = row.original;
        const tasks = job.tasks || [];
        const completedCount = tasks.filter(t => t.status === 'completed').length;
        const totalCount = tasks.length;

        return (
          <span className="text-sm">
            {completedCount}/{totalCount}
          </span>
        );
      },
    },
    {
      ...createSortableColumn<Job>('planned_start_at', 'Planned Start'),
      cell: ({ row }) => {
        const value = row.getValue('planned_start_at') as string;
        if (!value) return <span className="text-muted-foreground">-</span>;
        const date = new Date(value);
        if (isNaN(date.getTime())) return <span className="text-muted-foreground">-</span>;
        return date.toLocaleDateString();
      },
    },
    {
      ...createSortableColumn<Job>('planned_end_at', 'Planned End'),
      cell: ({ row }) => {
        const value = row.getValue('planned_end_at') as string;
        if (!value) return <span className="text-muted-foreground">-</span>;
        const date = new Date(value);
        if (isNaN(date.getTime())) return <span className="text-muted-foreground">-</span>;
        return date.toLocaleDateString();
      },
    },
    {
      ...createSortableColumn<Job>('created_at', 'Created At'),
      cell: ({ row }) => {
        const value = row.getValue('created_at') as string;
        if (!value) return <span className="text-muted-foreground">-</span>;
        const date = new Date(value);
        if (isNaN(date.getTime())) return <span className="text-muted-foreground">-</span>;
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const job = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => viewDetails(job)}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>
              {canManageJobs && (
                <>
                  <DropdownMenuItem onClick={() => navigate(`/jobs/${job.id}/edit`)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setSelectedJob(job);
                      setDeleteDialogOpen(true);
                    }}
                    className="text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="jobs" />

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold text-primary leading-tight mb-1">Jobs</h1>
            </div>
            {canManageJobs && (
              <Button onClick={() => setCreateModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Job
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div
            onClick={() => handleStatsFilterClick('all')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              statusFilter === 'all' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-primary">{stats?.total || 0}</div>
            <div className="text-sm text-muted-foreground">Total Jobs</div>
          </div>
          <div
            onClick={() => handleStatsFilterClick('assigned')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              statusFilter === 'assigned' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-blue-600">{stats?.assigned || 0}</div>
            <div className="text-sm text-muted-foreground">Assigned</div>
          </div>
          <div
            onClick={() => handleStatsFilterClick('in_progress')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              statusFilter === 'in_progress' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-yellow-600">{stats?.in_progress || 0}</div>
            <div className="text-sm text-muted-foreground">In Progress</div>
          </div>
          <div
            onClick={() => handleStatsFilterClick('completed')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              statusFilter === 'completed' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-green-600">{stats?.completed || 0}</div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </div>
        </div>

        {/* Jobs Table */}
        <DataTable
          columns={columns}
          data={jobs}
          loading={loading}
          searchKey="job_number"
          searchPlaceholder="Search jobs..."
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          serverSide={!hasActiveFilters}
          pagination={!hasActiveFilters ? pagination : undefined}
          onPaginationChange={!hasActiveFilters ? handlePaginationChange : undefined}
          onSortingChange={!hasActiveFilters ? handleSortingChange : undefined}
          toolbarContentLeft={
            <div className="flex gap-4">
              <Select
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={typeFilter}
                onValueChange={(value) => setTypeFilter(value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="delivery">Delivery</SelectItem>
                  <SelectItem value="pickup">Pickup</SelectItem>
                  <SelectItem value="installation">Installation</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          }
        />
      </main>

      {/* Assignment Modal */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Job</DialogTitle>
          </DialogHeader>

          {selectedJob && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Job Details</Label>
                <div className="text-sm text-muted-foreground">
                  {selectedJob.job_number} - {selectedJob.job_type ? selectedJob.job_type.toUpperCase() : 'TASK-BASED'}
                </div>
                <div className="text-sm text-muted-foreground">
                  Shipments: {selectedJob.shipments?.length || 0}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="driver">Driver *</Label>
                <Select
                  value={assignmentForm.driver_id}
                  onValueChange={(value) => setAssignmentForm({ ...assignmentForm, driver_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a driver" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.map((driver) => (
                      <SelectItem key={driver.id} value={driver.id}>
                        {driver.first_name} {driver.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="supervisor">Supervisor</Label>
                <Select
                  value={assignmentForm.supervisor_id}
                  onValueChange={(value) => setAssignmentForm({ ...assignmentForm, supervisor_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a supervisor (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {supervisors.map((supervisor) => (
                      <SelectItem key={supervisor.id} value={supervisor.id}>
                        {supervisor.first_name} {supervisor.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-4">
                <Button
                  variant="secondary"
                  onClick={() => setAssignModalOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignJob}
                  disabled={!assignmentForm.driver_id}
                  className="flex-1"
                >
                  Assign Job
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Job Modal */}
      <CreateJobModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSuccess={handleJobCreated}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteJob}
        title="Delete Job"
        description={`Are you sure you want to delete job ${selectedJob?.job_number}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Error Message Dialog */}
      <MessageDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        title="Error"
        description={errorMessage}
      />
    </div>
  );
};

export default Jobs;
